/**
 * White-label branding provider for the Client Portal (Module 3.6
 * Phase 5-7).
 *
 * The portal can render under any contractor's brand: logo, colours,
 * and a custom welcome message all come from
 * /api/v1/portal/branding (anonymous read so the login page is
 * already branded). After login we re-fetch from /branding/me to
 * pick up tenant-specific values resolved from the JWT.
 *
 * The provider exposes the resolved palette as CSS custom properties
 * on a wrapping div so any descendant can use
 * ``var(--portal-primary)`` / ``var(--portal-secondary)``.
 */
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { portalApi, type PortalBrandingPayload } from '@/api/portal'

const DEFAULT_BRANDING: PortalBrandingPayload = {
  tenant_id: 0,
  logo_url: null,
  primary_color: '#1E40AF',
  secondary_color: '#D4A856',
  accent_color: null,
  welcome_message_en: null,
  welcome_message_ar: null,
  company_name_en: 'BuildMaster',
  company_name_ar: 'بيلد ماستر',
  support_email: null,
  support_phone: null,
}

interface BrandingContextValue {
  branding: PortalBrandingPayload
  isLoading: boolean
  refresh: () => Promise<void>
}

const Ctx = createContext<BrandingContextValue>({
  branding: DEFAULT_BRANDING,
  isLoading: false,
  refresh: async () => {},
})

interface ProviderProps {
  children: ReactNode
  /** When the user is authenticated, swap to the /me variant so we get
   *  the tenant resolved from their JWT instead of the Host header. */
  authenticated?: boolean
}

export function BrandingProvider({
  children,
  authenticated = false,
}: ProviderProps) {
  const [branding, setBranding] = useState<PortalBrandingPayload>(DEFAULT_BRANDING)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = async () => {
    setIsLoading(true)
    try {
      const data = authenticated
        ? await portalApi.getBrandingMe()
        : await portalApi.getBrandingPublic()
      setBranding(data)
    } catch {
      // Branding is best-effort -- keep the previous (or default) palette
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated])

  const value = useMemo(
    () => ({ branding, isLoading, refresh }),
    [branding, isLoading],
  )

  const cssVars: Record<string, string> = {
    '--portal-primary': branding.primary_color || DEFAULT_BRANDING.primary_color,
    '--portal-secondary':
      branding.secondary_color || DEFAULT_BRANDING.secondary_color,
  }
  if (branding.accent_color) {
    cssVars['--portal-accent'] = branding.accent_color
  }

  return (
    <Ctx.Provider value={value}>
      <div style={cssVars as React.CSSProperties}>{children}</div>
    </Ctx.Provider>
  )
}

export function useBranding(): BrandingContextValue {
  return useContext(Ctx)
}
